<template>
  <div class="welcome-screen">
    <div v-if="hasImage" class="empty-state">
      <q-icon name="o_photo_filter" size="48px" color="grey-5" />
      <h2 class="empty-state-title">Choose a tool</h2>
      <p class="empty-state-text">Select a tool from the sidebar to enhance your image</p>
      <p class="empty-state-hint">Hover over the icons to see what each tool does</p>
    </div>

    <div v-else class="upload-state">
      <!-- Upload header -->
      <div class="upload-header">
        <q-icon name="o_auto_fix_high" size="48px" color="primary" />
        <h2 class="upload-title">AI Image Editor</h2>
        <p class="upload-description">Upload an image to start editing with AI-powered tools</p>
      </div>

      <!-- Show file preview and confirmation if file is selected -->
      <div v-if="selectedFile" class="file-preview-section">
        <div class="file-preview">
          <img v-if="filePreview" :src="filePreview" alt="File preview" class="preview-image" />
          <div class="file-info">
            <h4 class="file-name">{{ selectedFile.name }}</h4>
            <p class="file-size">{{ formatFileSize(selectedFile.size) }}</p>
          </div>
        </div>

        <div class="confirmation-actions">
          <q-btn flat no-caps label="Cancel" color="grey-7" @click="cancelUpload" class="cancel-btn" />
          <q-btn no-caps flat :loading="isUploading" label="Upload & Edit" text-color="secondary" @click="confirmUpload" class="confirm-btn" />
        </div>
      </div>

      <!-- Upload area (when no file selected) -->
      <div v-else>
        <div class="upload-area" :class="{ 'drag-over': isDragging }" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop" @click="triggerFileInput">
          <input type="file" ref="fileInput" @change="onFileSelected" accept="image/jpeg,image/jpg,image/webp" class="hidden-input" />

          <div class="upload-placeholder">
            <div class="upload-icon">
              <q-icon name="cloud_upload" size="32px" color="grey-5" />
            </div>
            <div class="upload-text">
              <h4 class="upload-main-text">Drop your image here</h4>
              <p class="upload-sub-text">or click to browse files</p>
            </div>
            <div class="upload-formats">JPEG, WebP supported</div>
          </div>
        </div>

        <!-- Upload help -->
        <div class="upload-help">
          <p class="help-text">
            <q-icon name="info_outline" size="14px" class="help-icon" />
            Max 10MB • Select an image to see preview and upload
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import fileUpload from '@/utils/fileUpload';

export default {
  name: 'EmptyState',
  props: {
    hasImage: {
      type: Boolean,
      default: true
    },
    isUploading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDragging: false,
      selectedFile: null,
      filePreview: null
    };
  },
  methods: {
    onDragOver(event) {
      event.preventDefault();
      this.isDragging = true;
    },

    onDragLeave(event) {
      event.preventDefault();
      this.isDragging = false;
    },

    async onDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.selectFile(files[0]);
      }
    },

    triggerFileInput() {
      if (this.isUploading) return;

      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/jpg,image/webp';
      input.onchange = async e => {
        const files = e.target.files;
        if (files.length > 0) {
          this.selectFile(files[0]);
        }
      };
      input.click();
    },

    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectFile(file);
      }
      event.target.value = '';
    },

    selectFile(file) {
      // Validate file type
      const supportedTypes = ['image/jpeg', 'image/jpg', 'image/webp'];
      if (!supportedTypes.includes(file.type)) {
        this.$q.notify({
          type: 'negative',
          message: 'Please select a valid image file (JPEG or WebP)',
          position: 'top',
          timeout: 3000
        });
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        this.$q.notify({
          type: 'negative',
          message: 'File size must be less than 10MB',
          position: 'top',
          timeout: 3000
        });
        return;
      }

      // Set the selected file and create preview
      this.selectedFile = file;
      this.filePreview = URL.createObjectURL(file);
    },

    async processFile(file) {
      try {
        // Emit to parent that upload is starting
        this.$emit('upload-started');

        // Auto-upload the file and get the fresh image ID
        const uploadedContent = await this.autoUploadImage(file);

        if (uploadedContent && uploadedContent.id) {
          // Create image object similar to gallery images
          const newImage = {
            id: uploadedContent.id,
            optimized_url: uploadedContent.optimized_url,
            preview_url: uploadedContent.preview_url,
            url: uploadedContent.url,
            name: file.name,
            size: file.size,
            type: file.type
          };

          // Clean up preview
          if (this.filePreview) {
            URL.revokeObjectURL(this.filePreview);
          }
          this.selectedFile = null;
          this.filePreview = null;

          // Emit the uploaded image to parent
          this.$emit('upload-success', newImage);
        } else {
          throw new Error('Upload failed - no content returned');
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        this.$emit('upload-error', error.message);
      }
    },

    // Auto-upload image to gallery and get fresh image ID
    async autoUploadImage(file) {
      let globalUpload = null;

      try {
        console.log('Starting automatic upload for dropped image:', file.name);

        // Add upload to GlobalUploadProgress tracking
        const globalUploads = await this.$store.dispatch('uploadProgress/addUploads', [file]);
        globalUpload = globalUploads[0]; // Get the first (and only) upload

        // Show GlobalUploadProgress dialog
        this.$store.dispatch('uploadProgress/showDialog');

        // Update progress for upload preparation phase
        await this.$store.dispatch('uploadProgress/updateUpload', {
          id: globalUpload.id,
          data: {
            status: 'uploading'
          }
        });

        // Upload the image using the existing fileUpload utility
        const uploadedContent = await fileUpload.uploadFile(file);

        if (!uploadedContent || !uploadedContent.id) {
          throw new Error('Invalid response from upload');
        }

        // Update global progress with real content ID
        await this.$store.dispatch('uploadProgress/updateUploadWithRealId', {
          tempId: globalUpload.id,
          realId: uploadedContent.id,
          data: {
            canDelete: true,
            contentType: uploadedContent.content_type || 'image'
          }
        });

        console.log('Image uploaded successfully:', uploadedContent);
        return uploadedContent;
      } catch (error) {
        console.error('Error during automatic image upload:', error);

        // Update global progress with error state if we have a reference
        if (globalUpload) {
          await this.$store.dispatch('uploadProgress/updateUpload', {
            id: globalUpload.id,
            data: {
              progress: 100,
              status: 'error',
              message: error.message || 'Failed to upload image'
            }
          });
        }
        throw error;
      }
    },

    cancelUpload() {
      this.selectedFile = null;
      this.filePreview = null;
    },

    confirmUpload() {
      if (this.selectedFile) {
        this.processFile(this.selectedFile);
      }
    },

    formatFileSize(size) {
      const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
      let unitIndex = 0;
      let sizeInBytes = size;

      while (sizeInBytes >= 1024 && unitIndex < units.length - 1) {
        sizeInBytes /= 1024;
        unitIndex++;
      }

      return `${sizeInBytes.toFixed(2)} ${units[unitIndex]}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.welcome-screen {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
  max-width: 320px;
  background: transparent;

  .empty-state-title {
    @include mona-sans-font($size: 22px, $bolded: true, $color: var(--main-text-color));
    margin: 16px 0 8px 0;
  }

  .empty-state-text {
    @include inter-font($size: 15px, $color: var(--secondary-text-color));
    margin: 0 0 16px 0;
    line-height: 1.5;
  }

  .empty-state-hint {
    @include inter-font($size: 13px, $color: var(--secondary-text-color));
    opacity: 0.7;
    margin: 0;
    line-height: 1.5;
    font-style: italic;
    background: rgba(248, 249, 250, 0.5);
    padding: 8px 12px;
    border-radius: 6px;
  }
}

.upload-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px;
  width: 100%;
  height: 100%;

  .upload-header {
    margin-bottom: 32px;

    .upload-title {
      @include mona-sans-font($size: 24px, $bolded: true, $color: var(--main-text-color));
      margin: 16px 0 8px 0;
      line-height: 1.2;
    }

    .upload-description {
      @include inter-font($size: 15px, $color: var(--secondary-text-color));
      margin: 0;
      line-height: 1.5;
    }
  }

  .upload-area {
    border: 2px dashed #e1e5e9;
    border-radius: 16px;
    padding: 40px 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    background: linear-gradient(145deg, #ffffff, #f8f9fa);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    position: relative;
    overflow: hidden;
    width: 100%;
    max-width: 360px;
    margin-bottom: 24px;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(254, 203, 47, 0.02) 0%, rgba(254, 203, 47, 0.05) 50%, rgba(254, 203, 47, 0.02) 100%);
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    }

    &.drag-over {
      border-color: var(--terziary-btn-color);
      background: linear-gradient(145deg, #fffdf7, #fefcf0);
      box-shadow: 0 12px 35px rgba(254, 203, 47, 0.2);

      &::before {
        opacity: 1;
      }

      .upload-icon {
        transform: scale(1.2);
        color: var(--terziary-btn-color) !important;
      }

      .upload-main-text {
        color: var(--terziary-btn-color);
      }
    }

    &.uploading {
      border-color: var(--tertiary-btn-color);
      background: linear-gradient(145deg, #fffdf7, #fefcf0);
      cursor: not-allowed;
    }

    .hidden-input {
      display: none;
    }

    .upload-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;

      .upload-icon {
        margin-bottom: 20px;
        padding: 16px;
        border-radius: 50%;
        background: linear-gradient(135deg, #f8f9fa, #e9ecef);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      .upload-text {
        margin-bottom: 20px;

        .upload-main-text {
          @include mona-sans-font($size: 20px, $bolded: true, $color: var(--secondary-text-color));
          margin: 0 0 8px 0;
          transition: color 0.3s ease;
        }

        .upload-sub-text {
          @include inter-font($size: 14px, $color: var(--secondary-text-color));
          margin: 0;
          line-height: 1.4;
        }
      }

      .upload-formats {
        @include inter-font($size: 12px, $color: var(--secondary-text-color));
        background: rgba(248, 249, 250, 0.8);
        padding: 6px 12px;
        border-radius: 20px;
        display: inline-block;
        border: 1px solid rgba(0, 0, 0, 0.05);
        font-weight: 500;
      }
    }
  }

  .upload-help {
    max-width: 320px;

    .help-text {
      @include inter-font($size: 13px, $color: var(--secondary-text-color));
      margin: 0;
      line-height: 1.5;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(248, 249, 250, 0.6);
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.05);

      .help-icon {
        margin-right: 8px;
        color: #6b7280;
        flex-shrink: 0;
      }
    }
  }

  .file-preview-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 360px;
    margin-bottom: 24px;

    .file-preview {
      background: white;
      border-radius: 12px;
      padding: 20px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border: 1px solid #e1e5e9;
      margin-bottom: 20px;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;

      .preview-image {
        max-width: 200px;
        max-height: 150px;
        border-radius: 8px;
        object-fit: cover;
        margin-bottom: 16px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .file-info {
        text-align: center;

        .file-name {
          @include mona-sans-font($size: 16px, $bolded: true, $color: var(--main-text-color));
          margin: 0 0 4px 0;
          word-break: break-all;
        }

        .file-size {
          @include inter-font($size: 14px, $color: var(--secondary-text-color));
          margin: 0;
        }
      }
    }

    .confirmation-actions {
      display: flex;
      gap: 12px;
      width: 100%;
      justify-content: center;

      .cancel-btn,
      .confirm-btn {
        @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
        height: 44px;
        border-radius: 8px;
        letter-spacing: 0.3px;
        padding: 0 24px;
        min-width: 120px;
      }

      .cancel-btn {
        border: 1px solid #e1e5e9;

        &:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }
      }

      .confirm-btn {
        background-color: var(--main-btn-color);
      }
    }
  }
}
</style>
